import type {
  IncidentAcknowledgeStatusEnum,
  IncidentDTO,
  IncidentListDTO,
  UpdateReadStatusIncidentBody,
} from '@eppendorf/vnls-notifications-utils';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
  Query,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { fetcher } from '$shared/fetch';
import { createPathWithQueryParams, INCIDENTS_BASE_PATH } from '$shared/utils/api-paths';

export const incidentsQueryKey = 'incidents';
export const unreadIncidentsCountQueryKey = 'unread-incidents-count';

export const createIncidentsQueryWithQueryParams = () => ({
  queryKey: [incidentsQueryKey],
  queryFn: ({ pageParam }: { pageParam: IncidentListDTO | undefined }) =>
    fetcher.get<IncidentListDTO[]>(
      createPathWithQueryParams(INCIDENTS_BASE_PATH, {
        isLazyLoading: true,
        pageSize: 10,
        ...(pageParam && {
          lastItemId: pageParam.id,
          lastItemCreatedAt: pageParam.createdAt,
        }),
      }),
    ),
  getNextPageParam: (lastPage: IncidentListDTO[], _pages: IncidentListDTO[][]) => {
    if (lastPage.length) {
      return lastPage.at(-1);
    }

    return undefined;
  },
  initialPageParam: undefined,
  staleTime: Infinity,
  placeholderData: keepPreviousData,
});

export const useIncidentsWithQueryParams = () =>
  useInfiniteQuery(createIncidentsQueryWithQueryParams());

export const useUnreadIncidentsCount = (enabled = true) =>
  useQuery({
    queryKey: [unreadIncidentsCountQueryKey],
    queryFn: () =>
      fetcher.get<{ unreadCount: number }>(`${INCIDENTS_BASE_PATH}/unread-count`),
    enabled,
    placeholderData: keepPreviousData,
    refetchInterval: 4000,
  });

type UpdateIncidentReadStatus = {
  incidentId: string;
  options: UpdateReadStatusIncidentBody;
};

export const useUpdateIncidentReadStatus = () => {
  const queryClient = useQueryClient();
  const updateIncidentReadStatus = ({ incidentId, options }: UpdateIncidentReadStatus) =>
    fetcher.patch<UpdateReadStatusIncidentBody, IncidentDTO>(
      `${INCIDENTS_BASE_PATH}/${incidentId}/actions/update-read-status`,
      options,
    );

  return useMutation<IncidentDTO, AxiosError, UpdateIncidentReadStatus>({
    mutationFn: updateIncidentReadStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [incidentsQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [unreadIncidentsCountQueryKey],
      });
    },
  });
};

// refetch more often if countdown is close to zero when using notification send delays
function calcRefetchInterval(incident?: IncidentDTO): number | false {
  const defaultInterval = 6000;
  const shortInterval = 1500;

  if (!incident) return defaultInterval;
  if (
    (incident.incidentStatus === 'PendingSendNotification' ||
      incident.incidentStatus === 'ActionRequired') &&
    incident.createdAt &&
    incident.notificationSendDelaySeconds
  ) {
    const endTime =
      incident.createdAt.valueOf() + incident.notificationSendDelaySeconds * 1000;
    if (Math.abs(Date.now() - endTime) < 2 * defaultInterval) {
      return shortInterval;
    }
  }
  if (
    incident.incidentStatus === 'ActionRequired' ||
    incident.incidentStatus === 'Escalated_1' ||
    incident.incidentStatus === 'Escalated_2' ||
    incident.incidentStatus === 'PendingSendNotification'
  ) {
    return defaultInterval;
  }
  return false;
}

export const createIncidentQuery = (incidentId: string) => ({
  queryKey: [incidentsQueryKey, incidentId],
  queryFn: () => fetcher.get<IncidentDTO>(`${INCIDENTS_BASE_PATH}/${incidentId}`),
  refetchInterval: (query: Query<IncidentDTO, Error, IncidentDTO, readonly unknown[]>) =>
    calcRefetchInterval(query.state.data),
});

export const useGetIncident = (incidentId: string) =>
  useQuery(createIncidentQuery(incidentId));

type AcknowledgeIncident = {
  incidentId: string;
};

export type AcknowledgeIncidentResponse = {
  message: string;
  type: IncidentAcknowledgeStatusEnum;
};

export const useAcknowledgeIncident = () => {
  const queryClient = useQueryClient();
  const acknowledgeIncident = ({ incidentId }: AcknowledgeIncident) =>
    fetcher.patch<null, IncidentDTO | AcknowledgeIncidentResponse>(
      `${INCIDENTS_BASE_PATH}/${incidentId}/actions/acknowledge`,
      null,
    );

  return useMutation<
    IncidentDTO | AcknowledgeIncidentResponse,
    AxiosError,
    AcknowledgeIncident
  >({
    mutationFn: acknowledgeIncident,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [incidentsQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [unreadIncidentsCountQueryKey],
      });
    },
  });
};
